import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Notification } from '../../../Notification';
import { PrimaryButton } from '../../../PrimaryButton';
import { SelectedBuilding } from '../../../../store/building/types';
import { SearchBar } from '../../../SearchBar';
import { ColumnArrow } from '../../../ColumnArrow';
import { Spinner } from '../../../Spinner';
import { ConnectedBuildingUserRow } from './components/BuildingUserRow';
import { BuildingUserActions } from '../../../../store/buildinguser';
import { BuildingUserOptionType, BuildingUserResponseType, UserScope } from '../../../../store/buildinguser/types';
import { ConnectedUserManagementModal } from './components/UserManagementModal/UserManagementModal';
import { ConnectedAddUserForm } from './components/AddUserForm';
import './UserManagement.css';
import { Pagination } from '../../../Pagination';

interface UserManagementFilterType {
  searchValue: string;
  sortValue: string;
  isReversed: boolean;
}

export interface State {
  filters: UserManagementFilterType;
  isAddNewUserOpen: boolean;
  inviteSent: boolean;
  isModalOpen: boolean;
  showResultPage: boolean;
  usersPerPage: number;
  currentPage: number;
  userAction: {
    type: string;
    apiLocation: string;
    email: string;
    identifier?: string;
  };
}

export interface Props {
  buildings: SelectedBuilding[];
  buildingUsers: BuildingUserResponseType;
  isLoading: boolean;
  userScopes: UserScope[];
  inviteCreated: boolean;
  getBuildingUsers(
    searchValue: string,
    sortValue: string,
    isReversed: boolean,
    usersPerPage: number,
    currentPage: number,
  ): void;
  getUserScopes(): void;
  executeUserAction(type: string, apiLocation: string, email: string): void;
  addUserPermission(apiLocation: string, identifier: string): void;
  removeUserPermission(apiLocation: string): void;
}

export class UserManagement extends Component<Props, State> {
  timeout = 0;

  state = {
    filters: { searchValue: '', sortValue: 'email', isReversed: false },
    isAddNewUserOpen: false,
    inviteSent: false,
    isModalOpen: false,
    showResultPage: false,
    usersPerPage: 10,
    currentPage: 1,
    userAction: {
      type: '',
      apiLocation: '',
      email: '',
      identifier: '',
    },
  };

  componentDidMount() {
    const { buildings, getUserScopes } = this.props;
    const tempBuildingOptions: BuildingUserOptionType[] = [];

    this.fetchBuildingUsers();
    getUserScopes();

    buildings.map((building) => {
      tempBuildingOptions.push({
        label: building.name,
        value: building.identifier,
      });
    });

    this.setState((state) => ({
      ...state,
      buildingOptions: tempBuildingOptions,
    }));
  }

  fetchBuildingUsers = () => {
    const {
      filters: { searchValue, sortValue, isReversed },
      usersPerPage,
      currentPage,
    } = this.state;
    const { getBuildingUsers } = this.props;

    getBuildingUsers(encodeURIComponent(searchValue), sortValue, isReversed, usersPerPage, currentPage);
  };

  openAddNewUser = () => {
    this.setState((state) => ({
      ...state,
      isAddNewUserOpen: true,
    }));
  };

  closeAddNewUser = () => {
    this.fetchBuildingUsers();

    this.setState((state) => ({
      ...state,
      isAddNewUserOpen: false,
    }));
  };

  handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget;
    if (this.timeout) clearTimeout(this.timeout);

    this.setState(
      (state) => ({
        ...state,
        filters: {
          ...state.filters,
          searchValue: value,
        },
      }),
      () => {
        this.timeout = window.setTimeout(() => {
          this.fetchBuildingUsers();
        }, 1000);
      },
    );
  };

  updateSortValue = (key: string) => {
    const { filters } = this.state;
    const isReversed = key === filters.sortValue && !filters.isReversed;

    this.setState(
      (state) => ({
        ...state,
        filters: {
          ...state.filters,
          isReversed,
          sortValue: key,
        },
      }),
      () => this.fetchBuildingUsers(),
    );
  };

  handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.currentTarget;

    this.setState((state) => ({
      ...state,
      [name]: value,
    }));
  };

  onRemovePermission = (permission: UserScope, email: string) => {
    if (permission.is_read_only) {
      return this.setState((state) => ({
        ...state,
        isModalOpen: true,
      }));
    }

    this.onUserAction('removePermission', permission.remove_api_location, email, '');
  };

  onAddPermission = (permission: BuildingUserOptionType, apiLocation: string, email: string) => {
    const { userScopes } = this.props;

    const addedScope: UserScope | undefined = userScopes.find((scope) => scope.identifier === permission.value);

    if (addedScope) {
      this.onUserAction('addPermission', apiLocation, email, addedScope.identifier);
    }
  };

  onUserAction = (type: string, apiLocation: string, email: string, identifier: string = '') => {
    this.setState((state) => ({
      ...state,
      isModalOpen: true,
      userAction: { type, apiLocation, email, identifier },
    }));
  };

  onModalCancel = () => {
    this.setState((state) => ({
      ...state,
      isModalOpen: false,
    }));
  };

  onModalClose = () => {
    this.setState(
      (state) => ({
        ...state,
        isModalOpen: false,
        userAction: {
          type: '',
          apiLocation: '',
          email: '',
          user: '',
        },
      }),
      () => this.fetchBuildingUsers(),
    );
  };

  onModalConfirm = () => {
    const {
      userAction: { type, apiLocation, email, identifier },
    } = this.state;
    const { executeUserAction, addUserPermission, removeUserPermission } = this.props;

    if (type === 'addPermission') {
      return addUserPermission(apiLocation, identifier);
    }
    if (type === 'removePermission') {
      return removeUserPermission(apiLocation);
    }

    executeUserAction(type, apiLocation, email);
  };

  changePage = (num: number) => {
    this.setState(
      (state) => ({
        ...state,
        currentPage: num,
      }),
      () => this.fetchBuildingUsers(),
    );
  };

  renderSwitch() {
    const { isAddNewUserOpen } = this.state;

    if (isAddNewUserOpen) {
      return <ConnectedAddUserForm closeAddNewUser={this.closeAddNewUser} />;
    }
    return this.renderDefault();
  }

  renderUsers() {
    const { buildingUsers } = this.props;

    if (buildingUsers.results.length === 0) {
      return (
        <div className="user-management__no-users-message">
          <Notification
            message="There are currently no building users setup for your buildings. Start by adding one."
            type="info"
          />
        </div>
      );
    }

    return (
      <div>
        {buildingUsers.results.map((user) => {
          const { uid_hash: uid } = user;

          return (
            <div key={uid}>
              <ConnectedBuildingUserRow
                user={user}
                onAction={this.onUserAction}
                onRemovePermission={this.onRemovePermission}
                onAddPermission={this.onAddPermission}
              />
            </div>
          );
        })}
      </div>
    );
  }

  renderDefault() {
    const {
      filters,
      isModalOpen,
      userAction: { type, email },
    } = this.state;
    const { isLoading, buildingUsers } = this.props;

    return (
      <React.Fragment>
        <div className="user-management">
          <div className="user-management__header">
            <div className="user-management__title">Building Users</div>
            <PrimaryButton icon="add-icon" label="Add New User" onClick={this.openAddNewUser} />
          </div>
          <div className="user-management__search">
            <SearchBar
              value={filters.searchValue}
              placeholderText="Search for a user by email"
              onChange={this.handleFilterChange}
            />
          </div>
          <div className="user-management__column-names">
            <button
              id="email"
              className={
                filters.sortValue === 'email'
                  ? 'user-management__column-name--wide user-management__column-name--selected'
                  : 'user-management__column-name--wide'
              }
              onClick={() => this.updateSortValue('email')}
            >
              <span className="user-management__column-name-text">EMAIL</span>
              <ColumnArrow value="email" sortValue={filters.sortValue} isReversed={filters.isReversed} />
            </button>
            <div className="user-management__column-name--wide">
              <span className="user-management__column-name-text">USER ACCESS</span>
            </div>
            <button
              id="building"
              className={
                filters.sortValue === 'building'
                  ? 'user-management__column-name--wide user-management__column-name--selected'
                  : 'user-management__column-name--wide'
              }
              onClick={() => this.updateSortValue('building')}
            >
              <span className="user-management__column-name-text">BUILDING</span>
              <ColumnArrow value="building" sortValue={filters.sortValue} isReversed={filters.isReversed} />
            </button>
            <div className="user-management__column-name">
              <span className="user-management__column-name-text">USER ROLE</span>
            </div>
            <div className="user-management__column-name">
              <span className="user-management__column-name-text">STATUS</span>
            </div>
            <span className="user-management__column-name-text">ACTIONS</span>
          </div>
          {isLoading ? (
            <div className="user-management--loading">
              <Spinner />
            </div>
          ) : (
            this.renderUsers()
          )}
          <Pagination
            currentPage={this.state.currentPage}
            itemsPerPage={10}
            numberOfItems={buildingUsers.count}
            onClick={this.changePage}
          />
          {isModalOpen ? (
            <ConnectedUserManagementModal
              operation={type}
              user={email}
              onClose={this.onModalClose}
              onCancel={this.onModalCancel}
              onConfirm={this.onModalConfirm}
            />
          ) : null}
        </div>
      </React.Fragment>
    );
  }

  render() {
    return <React.Fragment>{this.renderSwitch()}</React.Fragment>;
  }
}

export function mapStateToProps(state: any) {
  return {
    buildings: state.buildingReducer.buildingsList,
    buildingUsers: state.buildingUserReducer.buildingUsers,
    userScopes: state.buildingUserReducer.userScopes,
    inviteCreated: state.buildingUserReducer.inviteCreated,
    successMessage: state.buildingUserReducer.successMessage,
    isLoading: state.buildingUserReducer.isLoading,
  };
}

const mapDispatchToProps = {
  getBuildingUsers: BuildingUserActions.getBuildingUsers,
  getUserScopes: BuildingUserActions.getUserScopes,
  executeUserAction: BuildingUserActions.executeUserAction,
  addUserPermission: BuildingUserActions.addUserPermission,
  removeUserPermission: BuildingUserActions.removeUserPermission,
};

export const ConnectedUserManagement = connect(mapStateToProps, mapDispatchToProps)(UserManagement);
